import axios from 'axios';
import { AuthResponse, Category, Product, Stock, PaginatedResponse, User, Role, Order, OrderStatus, PaymentType, Shop, ShopPayment } from '../types';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3000');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (phoneNumber: string, password: string): Promise<AuthResponse> =>
    api.post('/auth/login', { phoneNumber, password }).then((res) => res.data),
  
  register: (data: {
    phoneNumber: string;
    password: string;
    firstName: string;
    lastName?: string;
  }): Promise<AuthResponse> =>
    api.post('/auth/register', data).then((res) => res.data),
  
  getProfile: (): Promise<any> =>
    api.get('/auth/profile').then((res) => res.data),
};

export const categoriesAPI = {
  getAll: (params?: { withDeleted?: boolean }): Promise<Category[]> =>
    api.get('/categories', { params }).then((res) => res.data),

  getOne: (id: number): Promise<Category> =>
    api.get(`/categories/${id}`).then((res) => res.data),

  create: (data: { name: string; description?: string }): Promise<Category> =>
    api.post('/categories', data).then((res) => res.data),

  update: (id: number, data: { name?: string; description?: string }): Promise<Category> =>
    api.put(`/categories/${id}`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/categories/${id}`).then(() => undefined),

  restore: (id: number): Promise<Category> =>
    api.patch(`/categories/${id}/restore`).then((res) => res.data),
};

export const productsAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    categoryId?: number;
    withDeleted?: boolean;
  }): Promise<PaginatedResponse<Product>> =>
    api.get('/products', { params }).then((res) => res.data),
  
  getOne: (id: number): Promise<Product> =>
    api.get(`/products/${id}`).then((res) => res.data),
  
  create: (data: {
    categoryId: number;
    name: string;
    unit: 'PIECE' | 'METER' | 'KG';
    price?: number;
    costPrice?: number;
    attributes?: Array<{ key: string; value: string }>;
  }): Promise<Product> =>
    api.post('/products', data).then((res) => res.data),

  update: (id: number, data: {
    categoryId?: number;
    name?: string;
    unit?: 'PIECE' | 'METER' | 'KG';
    price?: number;
    costPrice?: number;
    attributes?: Array<{ key: string; value: string }>;
  }): Promise<Product> =>
    api.put(`/products/${id}`, data).then((res) => res.data),
  
  delete: (id: number): Promise<void> =>
    api.delete(`/products/${id}`).then(() => undefined),

  restore: (id: number): Promise<Product> =>
    api.patch(`/products/${id}/restore`).then((res) => res.data),
};

export const stockAPI = {
  getByProduct: (productId: number): Promise<Stock> =>
    api.get(`/stock/product/${productId}`).then((res) => res.data),
  
  set: (productId: number, quantity: number): Promise<Stock> =>
    api.post(`/stock/product/${productId}/set`, { quantity }).then((res) => res.data),
  
  increase: (productId: number, amount: number): Promise<Stock> =>
    api.post(`/stock/product/${productId}/increase`, { amount }).then((res) => res.data),
  
  decrease: (productId: number, amount: number): Promise<Stock> =>
    api.post(`/stock/product/${productId}/decrease`, { amount }).then((res) => res.data),
};

export const usersAPI = {
  getAll: (params?: { withDeleted?: boolean }): Promise<User[]> =>
    api.get('/users', { params }).then((res) => res.data),
  
  getOne: (id: number): Promise<User> =>
    api.get(`/users/${id}`).then((res) => res.data),
  
  create: (data: {
    phoneNumber: string;
    password: string;
    firstName: string;
    lastName?: string;
    roleIds: number[];
  }): Promise<User> =>
    api.post('/users', data).then((res) => res.data),
  
  update: (id: number, data: {
    phoneNumber?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    roleIds?: number[];
  }): Promise<User> =>
    api.put(`/users/${id}`, data).then((res) => res.data),
  
  delete: (id: number): Promise<void> =>
    api.delete(`/users/${id}`).then(() => undefined),

  restore: (id: number): Promise<User> =>
    api.patch(`/users/${id}/restore`).then((res) => res.data),
};

export const rolesAPI = {
  getAll: (): Promise<Role[]> =>
    api.get('/roles').then((res) => res.data),
  
  getOne: (id: number): Promise<Role> =>
    api.get(`/roles/${id}`).then((res) => res.data),
};

export const ordersAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: OrderStatus;
    agentId?: number;
    shopId?: number;
    overdue?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<PaginatedResponse<Order>> =>
    api.get('/orders', { params }).then((res) => res.data),

  getOne: (id: number): Promise<Order> =>
    api.get(`/orders/${id}`).then((res) => res.data),

  create: (data: {
    items: Array<{ productId: number; quantity: number }>;
    notes?: string;
    shopId?: number;
    paymentType?: PaymentType;
    dueDate?: string;
  }): Promise<Order> =>
    api.post('/orders', data).then((res) => res.data),

  update: (id: number, data: {
    items?: Array<{ productId: number; quantity: number }>;
    notes?: string;
    shopId?: number | null;
    agentId?: number;
    paymentType?: PaymentType;
    dueDate?: string | null;
  }): Promise<Order> =>
    api.put(`/orders/${id}`, data).then((res) => res.data),

  updateStatus: (id: number, data: {
    status: OrderStatus;
    notes?: string;
    rejectionReason?: string;
  }): Promise<Order> =>
    api.put(`/orders/${id}/status`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/orders/${id}`).then(() => undefined),

  restore: (id: number): Promise<Order> =>
    api.patch(`/orders/${id}/restore`).then((res) => res.data),
};

export const shopsAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    ownerId?: number;
    isActive?: boolean;
    withDeleted?: boolean;
  }): Promise<PaginatedResponse<Shop>> =>
    api.get('/shops', { params }).then((res) => res.data),

  getOne: (id: number): Promise<Shop> =>
    api.get(`/shops/${id}`).then((res) => res.data),

  create: (data: {
    name: string;
    description?: string;
    address?: string;
    phone?: string;
    isActive?: boolean;
    ownerId?: number;
  }): Promise<Shop> =>
    api.post('/shops', data).then((res) => res.data),

  update: (id: number, data: {
    name?: string;
    description?: string;
    address?: string;
    phone?: string;
    isActive?: boolean;
    ownerId?: number;
    creditLimit?: number;
    maxDebtDays?: number;
  }): Promise<Shop> =>
    api.put(`/shops/${id}`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/shops/${id}`).then(() => undefined),

  restore: (id: number): Promise<Shop> =>
    api.patch(`/shops/${id}/restore`).then((res) => res.data),

  addPayment: (shopId: number, data: { amount: number; notes?: string }): Promise<ShopPayment> =>
    api.post(`/shops/${shopId}/payments`, data).then((res) => res.data),

  getPayments: (shopId: number): Promise<ShopPayment[]> =>
    api.get(`/shops/${shopId}/payments`).then((res) => res.data),
};

export default api;
